import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import { pushErrors, pushGetSrcProfileRequest, pushGetSrcProfileResponse } from 'common/gamp';
import { trackError, trackEvent } from 'common/observability';
import { saveMeta, saveProfile, saveUser, UserProfile } from 'communicator/store';
import {
  getCardListPayload,
  getDcfExperience,
  getFullAccessToken,
  getIsNonVisaRecognizedUser,
  getIsUserLocked,
  getUser,
  getVIntentId
} from 'communicator/store/selectors';
import { fetchCardList } from 'communicator/utils/api/card-list';
import { getHeader } from 'communicator/utils/fetch';
import getGenericError from 'communicator/utils/generic-error';
import { getOrchestratorLockoutFlow } from 'communicator/utils/get-lockout-flow';
import { getMaskedCardsFromCardList } from 'communicator/utils/get-masked-cards';
import { parseIdToken } from 'communicator/utils/id-token';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import parseExpiryFromAccessToken from 'communicator/utils/parse-expiry-from-token';
import { localStorage, STARTING_TAB, WELCOME_TABS } from 'communicator/utils/storage';
import { GetSrcProfileData, GetSrcProfilePayload } from 'types/visa-sdk/get-src-profile';
import legacyPostIntentGetSrcProfile from './legacy-get-src-profile';

/**
 * Takes an idTokens array, parses out the email hash, and groups all tokens by
 * email hash. Once grouped, return an array of the first token from each group.
 *
 * Not sure why this is the algorithm we use.
 * https://stash.trusted.visa.com:7990/projects/DMPDUIE/repos/system-sdk/pull-requests/213/overview
 */
function groupTokens(tokens: string[]) {
  if (!tokens.length) {
    return [];
  }

  const tokensByEmailHash: Record<string, string[]> = {};
  const result = [];

  for (const token of tokens) {
    // 1. token must have 3 segments header, claim and signature (ie. header.claim.signature)
    // 2. the claim must have an email key with a value
    const splitToken = token.split('.');

    if (splitToken.length === 3) {
      const claim = splitToken[1];
      let decodedClaim;
      let hashedEmail;

      try {
        decodedClaim = JSON.parse(window.atob(claim));
      } finally {
        hashedEmail = decodedClaim?.email as string | undefined;
      }

      if (!hashedEmail) {
        continue;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (tokensByEmailHash[hashedEmail]) {
        // put idTokens with same emailHash in one group
        tokensByEmailHash[hashedEmail].push(token);
      } else {
        tokensByEmailHash[hashedEmail] = [token];
      }
    }
  }

  for (const key in tokensByEmailHash) {
    result.push({ key, value: tokensByEmailHash[key] });
  }

  result.sort((a, b) => b.value.length - a.value.length);
  return result.map(element => element.value[0]);
}

export default async function getSrcProfile(
  event: HandlerEvent<GetSrcProfileData>
): Promise<GetSrcProfilePayload | ValidationError> {
  try {
    markStart('GET_SRC_PROFILE');
    markStart('GET_SRC_PROFILE_LEGACY');
    pushGetSrcProfileRequest();
    const stime = Date.now();
    const idTokens = groupTokens(event.data?.idTokens ?? []);
    const otpProvider = event.data?.otpProvider;

    if (otpProvider) {
      saveMeta({ otpProvider });
    }

    localStorage.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);

    // handle common errors
    if (getIsUserLocked()) {
      markEnd('GET_SRC_PROFILE', { doFlush: true, failure: true, reason: 'USER_LOCKED' });
      // START GENAI@GHCOPILOT
      pushErrors('GET_SRC_PROFILE', 'USER_LOCKED');
      // END GENAI@GHCOPILOT
      // START GENAI@GHCOPILOT
      trackError('get-src-profile:failure', {
        reason: 'USER_LOCKED',
        idTokens: idTokens.length,
        otpProvider
      });

      // END GENAI@GHCOPILOT
      return {
        profiles: [],
        srcCorrelationId: getVIntentId()
      };
    }

    if (
      // TODO: VCO-16158 - /cardList does not currently return non-tokenized
      // cards, so will not work for hybrid integrations. Remove this check once
      // support is enabled.
      getDcfExperience() !== 'integrated'
    ) {
      return legacyPostIntentGetSrcProfile({ idTokens });
    }

    const fullAccessToken = getFullAccessToken();
    const cardListPayload = getCardListPayload(idTokens);

    //Removed spawn api call which got invoked immediately after fetchcards,
    // SRC-orchestartor will make a spawn call as part of cardlist api(/profiles/prepare)
    const cardListResponse = await fetchCardList({
      accessToken: fullAccessToken,
      payload: cardListPayload
    });

    pushGetSrcProfileResponse(Date.now() - stime);

    if (!cardListResponse.ok) {
      if (getOrchestratorLockoutFlow(cardListResponse)) {
        saveUser({
          accessToken: null,
          isLocked: true
        });

        markEnd('GET_SRC_PROFILE', { doFlush: true, failure: true, reason: 'AUTH_INVALID' });
        // START GENAI@GHCOPILOT
        pushErrors('GET_SRC_PROFILE', 'AUTH_INVALID');
        // END GENAI@GHCOPILOT
        // START GENAI@GHCOPILOT
        trackError('get-src-profile:failure', {
          idTokens: idTokens.length,
          otpProvider,
          reason: 'AUTH_INVALID',
          status: cardListResponse.status
        });

        // END GENAI@GHCOPILOT
        return error('AUTH_INVALID');
      }

      const err = getGenericError(cardListResponse);
      markEnd('GET_SRC_PROFILE', { doFlush: true, failure: true, reason: err });
      // START GENAI@GHCOPILOT
      pushErrors('GET_SRC_PROFILE', err);
      trackError('get-src-profile:failure', {
        idTokens: idTokens.length,
        otpProvider,
        reason: err,
        status: cardListResponse.status
      });

      // END GENAI@GHCOPILOT
      return error(err);
    }

    if (cardListResponse.data.srcCorrelationId) {
      saveMeta({
        vIntentId: cardListResponse.data.srcCorrelationId
      });
    }

    // Visa will only return a single profile
    const srcProfile = cardListResponse.data.profiles[0];

    // In integrated experiences we will need to parse out shippingAddressId for PUT intent
    // Also might need the selected card for rendering in step up flows
    const dcfExperience = getDcfExperience();

    if (dcfExperience === 'integrated') {
      saveProfile({
        maskedShippingAddresses: srcProfile.maskedShippingAddresses,
        maskedCards: srcProfile.maskedCards
      });

      const identityType = srcProfile.maskedConsumer.maskedConsumerIdentity.identityType;

      saveUser({
        identityType
      });
    }

    const newAccessToken = getHeader(cardListResponse, 'access_token');
    const { amrCookie, emailHash } = parseIdToken(srcProfile.authorization);
    const userUpdates: UserProfile = {};

    //for tnc user access token will be undefined

    if (newAccessToken) {
      const expiresIn = parseExpiryFromAccessToken(newAccessToken) as number;
      userUpdates.accessToken = {
        expiresIn,
        isPartial: false,
        token: newAccessToken
      };
    }

    const refreshedIdToken = srcProfile.authorization;

    if (refreshedIdToken) {
      userUpdates.idToken = refreshedIdToken;
    }

    /*
     * VCO-14507 need to only set flag when MA SSI, not MA OTP login. For MA
     * SSI user, otpProvider is defaulted to VISA since no otp was sent. in
     * Visa use cases, this fn is not called
     */
    if (getIsNonVisaRecognizedUser(amrCookie)) {
      userUpdates.isNonVisaRecognizedUser = true;
    }

    if (emailHash) {
      userUpdates.emailHash = emailHash;
    }

    saveUser(userUpdates);

    markEnd('GET_SRC_PROFILE', { doFlush: true });
    // START GENAI@GHCOPILOT
    trackEvent('get-src-profile:success', {
      idTokens: idTokens.length,
      isNonVisaRecognizedUser: userUpdates.isNonVisaRecognizedUser,
      isVisaSrci: false,
      otpProvider
    });
    // END GENAI@GHCOPILOT

    return {
      profiles: [
        {
          // `profile.idToken` was superceded by `profile.authorization` in the
          // EMVCo spec but remains here for legacy purposes. This idToken is
          // passed to checkout() when a user that exists in Visa's system tries
          // to add a new card on a different network in which they are not
          // recognized.
          idToken: getUser()?.idToken,
          maskedCards: getMaskedCardsFromCardList(srcProfile),
          maskedConsumer: srcProfile.maskedConsumer
          // maskedShippingAddresses is part of the EMVCo spec, but not Visa's
          // integration guide
          // maskedShippingAddresses: srcProfile.maskedShippingAddresses
        }
      ],
      srcCorrelationId: cardListResponse.data.srcCorrelationId
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    pushErrors('GET_SRC_PROFILE', err);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('GET_SRC_PROFILE', { doFlush: true, failure: true, reason: sErr });
    // START GENAI@GHCOPILOT
    trackError('get-src-profile:failure', {
      idTokens: event.data?.idTokens?.length || 0,
      otpProvider: event.data?.otpProvider,
      reason: sErr,
      status: (err as { status?: number }).status || 500
    });

    // END GENAI@GHCOPILOT
    return error('GET_SRC_PROFILE_FAILED', sErr);
    // END GENAI@GHCOPILOT
  }
}
